import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Activity as ActivityIcon, AlertTriangle, CalendarClock, Check, ChevronDown, ChevronRight, Diamond, ExternalLink, FolderKanban, GanttChart,
  LayoutGrid, List, Megaphone, Pin, Plus, Scale, Trash2, UserPlus, UserX } from 'lucide-react';
import { api, type Activity, type Health, type Me, type Person, type Project, type Resource, type Status, type Task } from '../api.ts';
import { useDashboard, useSave, useTaskDrawer } from '../data.ts';
import { ago, basisLabel, firstName, fmtDate, isOverdue, recentlyByAgent, statusLabel, today } from '../util.ts';
import { Gantt } from '../components/Gantt.tsx';
import { Avatar, CommentCount, DateText, Empty, Err, OpsMark, PriorityFlag } from '../components/ui.tsx';

export const healthLabel: Record<Health, string> = { on_track: 'On track', at_risk: 'At risk', off_track: 'Off track' };
const STALE_DAYS = 14;
const daysUntil = (iso: string) => Math.round((Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${today()}T00:00:00Z`)) / 86400_000);
const inDays = (n: number) => (n === 0 ? 'today' : n === 1 ? 'tomorrow' : n > 0 ? `in ${n}d` : `${-n}d late`);
const byDate = (a: Task, b: Task) => (a.end_date ?? a.start_date ?? '9999').localeCompare(b.end_date ?? b.start_date ?? '9999');

// Everything the project views show, worked out once from the project's live tasks.
// Health is the partners' own call when they posted one in the last two weeks; otherwise it's read from the plan.
export function projectStats(p: Project, all: Task[]) {
  const tasks = all.filter((t) => t.project === p.id && t.status !== 'cancelled');
  const open = tasks.filter((t) => t.status !== 'done');
  const overdue = open.filter(isOverdue);
  const blocked = open.filter((t) => t.status === 'blocked');
  const unowned = open.filter((t) => !t.owner);
  const undated = open.filter((t) => t.date_basis === 'unknown');
  const soon = open.filter((t) => t.end_date && daysUntil(t.end_date) >= 0 && daysUntil(t.end_date) <= 7);
  const milestones = tasks.filter((t) => t.kind === 'milestone').sort(byDate);
  const next = milestones.find((t) => t.status !== 'done') ?? null;
  const done = tasks.length - open.length;
  const auto: Health | null = !open.length ? null
    : overdue.some((t) => t.kind === 'milestone') || overdue.length + blocked.length >= 3 ? 'off_track'
    : overdue.length + blocked.length > 0 ? 'at_risk' : 'on_track';
  const fresh = p.status && Date.now() - Date.parse(p.status.at) < STALE_DAYS * 86400_000 ? p.status : null;
  const last = tasks.map((t) => t.updated_at).sort().at(-1) ?? null;
  return { tasks, open, overdue, blocked, unowned, undated, soon, milestones, next, done, pct: tasks.length ? Math.round((done / tasks.length) * 100) : 0,
    health: fresh?.health ?? auto, manual: !!fresh, auto, last };
}
type Stats = ReturnType<typeof projectStats>;

export function HealthPill({ health, manual }: { health: Health | null; manual?: boolean }) {
  if (!health) return <span className="hpill h-none">No open work</span>;
  return <span className={`hpill h-${health}`} title={manual ? 'Set in the latest status update' : 'Read from overdue and blocked work'}><i />{healthLabel[health]}{!manual && <em>auto</em>}</span>;
}

function Ring({ pct, color, size = 46 }: { pct: number; color: string; size?: number }) {
  const r = (size - 6) / 2, c = 2 * Math.PI * r;
  return (
    <svg className="ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${pct}% done`}>
      <circle cx={size / 2} cy={size / 2} r={r} className="ring-bg" />
      {pct > 0 && <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeDasharray={`${(pct / 100) * c} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />}
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle">{pct}%</text>
    </svg>
  );
}

// Share of the project's work in each state, as one thin bar.
function StatusBar({ s }: { s: Stats }) {
  const total = s.tasks.length || 1;
  const parts = (['done', 'active', 'blocked', 'planned'] as Status[]).map((k) => ({ k, n: s.tasks.filter((t) => t.status === k).length }));
  return (
    <div className="sbar" title={parts.map((x) => `${x.n} ${statusLabel[x.k].toLowerCase()}`).join(' · ')}>
      {parts.map((x) => x.n > 0 && <i key={x.k} className={`sb-${x.k}`} style={{ width: `${(x.n / total) * 100}%` }} />)}
    </div>
  );
}

// ---------- Portfolio ----------

export function ProjectsPage({ me }: { me: Me }) {
  const d = useDashboard();
  const [adding, setAdding] = useState(false);
  if (!d.data) return <div className="page"><Err e={d.error} /></div>;
  const { projects, tasks, people } = d.data;
  const stats = projects.map((p) => ({ p, s: projectStats(p, tasks) }));
  const count = (h: Health) => stats.filter((x) => x.s.health === h).length;
  const unowned = stats.reduce((n, x) => n + x.s.unowned.length, 0);
  const stale = stats.filter((x) => x.s.open.length && !x.s.manual).length;
  return (
    <div className="page">
      <header className="page-h row">
        <div><h1>Projects</h1><p className="muted">Every workstream at a glance. Click in to plan, assign and post a status update.</p></div>
        <span className="spacer" />
        {me.role === 'owner' && <button className="btn primary" onClick={() => setAdding(true)}><Plus size={14} aria-hidden /> New project</button>}
      </header>
      <div className="pf-strip">
        <span className="hpill h-on_track"><i />{count('on_track')} on track</span>
        <span className="hpill h-at_risk"><i />{count('at_risk')} at risk</span>
        <span className="hpill h-off_track"><i />{count('off_track')} off track</span>
        <span className="spacer" />
        {unowned > 0 && <span className="pf-nudge"><UserX size={13} aria-hidden /> {unowned} open tasks have no owner</span>}
        {stale > 0 && <span className="pf-nudge"><Megaphone size={13} aria-hidden /> {stale} project{stale > 1 ? 's' : ''} with no update in {STALE_DAYS} days</span>}
      </div>
      {adding && <NewProject onDone={() => setAdding(false)} people={people} />}
      <div className="pf-grid">{stats.map(({ p, s }) => <ProjectCard key={p.id} p={p} s={s} people={people} />)}</div>
    </div>
  );
}

function ProjectCard({ p, s, people }: { p: Project; s: Stats; people: Person[] }) {
  const n = s.next?.end_date ? daysUntil(s.next.end_date) : null;
  return (
    <Link to={`/projects/${p.id}`} className="pcard" style={{ '--c': p.color } as React.CSSProperties}>
      <div className="pcard-h">
        <span className="key">{p.key}</span><b>{p.title}</b><span className="spacer" />
        {p.owner ? <Avatar email={p.owner} people={people} size={22} /> : <span className="muted sm">No lead</span>}
      </div>
      <p className="pcard-desc clamp">{p.description || 'No description yet.'}</p>
      <div className="pcard-body">
        <Ring pct={s.pct} color={p.color} />
        <div className="pcard-next">
          <span className="muted sm">Next milestone</span>
          {s.next ? <><b className="clamp1">{s.next.title}</b><span className={`sm ${n !== null && n < 0 ? 'bad' : 'muted'}`}>{s.next.end_date ? `${fmtDate(s.next.end_date)} · ${inDays(n!)} · ${basisLabel[s.next.date_basis].toLowerCase()}` : 'No date yet'}</span></>
            : <b className="muted">None planned</b>}
        </div>
      </div>
      <StatusBar s={s} />
      <div className="pcard-chips">
        <span><b>{s.open.length}</b> open</span>
        {s.soon.length > 0 && <span><b>{s.soon.length}</b> due this week</span>}
        {s.overdue.length > 0 && <span className="bad"><b>{s.overdue.length}</b> overdue</span>}
        {s.blocked.length > 0 && <span className="bad"><b>{s.blocked.length}</b> blocked</span>}
        {s.unowned.length > 0 && <span className="warn"><b>{s.unowned.length}</b> unowned</span>}
      </div>
      <div className="pcard-f">
        <HealthPill health={s.health} manual={s.manual} />
        {p.status ? <span className="clamp1 muted sm">“{p.status.text}” · {firstName(p.status.by, people)} {ago(p.status.at)}</span>
          : <span className="muted sm">No status update yet</span>}
      </div>
    </Link>
  );
}

function NewProject({ onDone, people }: { onDone: () => void; people: Person[] }) {
  const save = useSave();
  const [f, setF] = useState({ key: '', title: '', description: '', color: '#4f6bed', owner: '' });
  return (
    <form className="card new-project" onSubmit={async (e) => { e.preventDefault(); await save.mutateAsync({ path: '/projects', body: { ...f, key: f.key.toUpperCase(), owner: f.owner || null } }); onDone(); }}>
      <div className="row">
        <input value={f.key} onChange={(e) => setF({ ...f, key: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8) })} placeholder="KEY" aria-label="Key" required style={{ width: 90 }} />
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Project name" aria-label="Project name" required style={{ flex: 1 }} />
        <input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} aria-label="Color" />
        <select value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} aria-label="Lead"><option value="">No lead</option>{people.map((x) => <option key={x.email} value={x.email}>{x.name || x.email}</option>)}</select>
      </div>
      <textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What is this workstream for?" aria-label="Description" />
      <div className="row"><button className="btn primary" disabled={save.isPending}>Create project</button><button type="button" className="btn ghost" onClick={onDone}>Cancel</button></div>
      <Err e={save.error} />
    </form>
  );
}

// ---------- One project ----------

const VIEWS = [
  { id: 'overview', label: 'Overview', icon: FolderKanban },
  { id: 'list', label: 'List', icon: List },
  { id: 'board', label: 'Board', icon: LayoutGrid },
  { id: 'timeline', label: 'Timeline', icon: GanttChart },
  { id: 'activity', label: 'Activity', icon: ActivityIcon },
] as const;
type View = (typeof VIEWS)[number]['id'];

export function ProjectPage({ me }: { me: Me }) {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const view = (VIEWS.some((v) => v.id === params.get('view')) ? params.get('view') : 'overview') as View;
  const setView = (v: View) => setParams((p) => { if (v === 'overview') p.delete('view'); else p.set('view', v); return p; });
  const d = useDashboard();
  const save = useSave();
  const [posting, setPosting] = useState(false);
  if (!d.data) return <div className="page"><Err e={d.error} /></div>;
  const p = d.data.projects.find((x) => x.id === id);
  if (!p) return <div className="page"><p>No such project. <Link to="/projects">All projects →</Link></p></div>;
  const s = projectStats(p, d.data.tasks);
  const { people } = d.data;
  const tellOps = () => window.dispatchEvent(new CustomEvent('omni:open', { detail: { text: `${p.key}: `, kind: 'note' } }));

  return (
    <div className="page proj" style={{ '--c': p.color } as React.CSSProperties}>
      <nav className="crumbs sm"><Link to="/projects">Projects</Link> <ChevronRight size={12} aria-hidden /> <span>{p.key}</span></nav>
      <header className="proj-h">
        <span className="proj-badge">{p.key}</span>
        <div className="proj-title">
          <h1>{p.title}</h1>
          <EditableText value={p.description} placeholder="Add a description" onSave={(description) => save.mutate({ path: `/projects/${p.id}`, method: 'PATCH', body: { description } })} />
        </div>
        <div className="proj-actions">
          <HealthPill health={s.health} manual={s.manual} />
          <label className="lead">Lead
            <select value={p.owner ?? ''} onChange={(e) => save.mutate({ path: `/projects/${p.id}`, method: 'PATCH', body: { owner: e.target.value || null } })}>
              <option value="">No lead</option>{people.map((x) => <option key={x.email} value={x.email}>{x.name || x.email}</option>)}</select></label>
          <button className="btn" onClick={tellOps}><OpsMark size={14} aria-hidden /> Tell Ops</button>
          <button className="btn primary" onClick={() => { setPosting(true); setView('overview'); }}><Megaphone size={14} aria-hidden /> Post update</button>
        </div>
      </header>

      <div className="proj-stats">
        <Ring pct={s.pct} color={p.color} size={54} />
        <div><b>{s.done}<small>/{s.tasks.length}</small></b><span>done</span></div>
        <div><b>{s.soon.length}</b><span>due in 7 days</span></div>
        <div className={s.overdue.length ? 'bad' : ''}><b>{s.overdue.length}</b><span>overdue</span></div>
        <div className={s.blocked.length ? 'bad' : ''}><b>{s.blocked.length}</b><span>blocked</span></div>
        <div className={s.unowned.length ? 'warn' : ''}><b>{s.unowned.length}</b><span>no owner</span></div>
        <div className="grow"><span>Next milestone</span>{s.next ? <b className="clamp1 sm-b">{s.next.title}{s.next.end_date && <small> · {fmtDate(s.next.end_date)} ({inDays(daysUntil(s.next.end_date))})</small>}</b> : <b className="muted sm-b">None planned</b>}</div>
      </div>

      <div className="tabs" role="tablist">
        {VIEWS.map((v) => (
          <button key={v.id} role="tab" aria-selected={view === v.id} className={view === v.id ? 'on' : ''} onClick={() => setView(v.id)}>
            <v.icon size={14} aria-hidden /> {v.label}{v.id === 'list' && <span className="count">{s.open.length}</span>}
          </button>
        ))}
      </div>

      {view === 'overview' && <Overview p={p} s={s} me={me} people={people} resources={d.data.resources.filter((r) => r.project === p.id)} posting={posting} setPosting={setPosting} />}
      {view === 'list' && <ListView p={p} s={s} me={me} people={people} />}
      {view === 'board' && <BoardView s={s} people={people} />}
      {view === 'timeline' && <section className="card flush"><Gantt tasks={s.tasks} projects={[p]} height={560} /></section>}
      {view === 'activity' && <ActivityView p={p} people={people} />}
    </div>
  );
}

function EditableText({ value, placeholder, onSave }: { value: string; placeholder: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (!editing) return <p className={`proj-desc ${value ? '' : 'muted'}`} onClick={() => setEditing(true)} title="Click to edit">{value || placeholder}</p>;
  const done = () => { setEditing(false); if (v.trim() !== value) onSave(v.trim()); };
  return <textarea className="proj-desc-in" autoFocus rows={2} value={v} onChange={(e) => setV(e.target.value)} onBlur={done}
    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); done(); } if (e.key === 'Escape') { setV(value); setEditing(false); } }} />;
}

// ---------- Overview ----------

function Overview({ p, s, me, people, resources, posting, setPosting }: { p: Project; s: Stats; me: Me; people: Person[]; resources: Resource[]; posting: boolean; setPosting: (b: boolean) => void }) {
  const upNext = s.open.filter((t) => t.end_date).sort(byDate).slice(0, 6);
  return (
    <>
      <MilestoneRoad s={s} />
      <div className="proj-grid">
        <div>
          <StatusCard p={p} s={s} people={people} posting={posting} setPosting={setPosting} />
          <Attention s={s} me={me} people={people} />
        </div>
        <div>
          <section className="card">
            <div className="card-h"><h2><CalendarClock size={14} aria-hidden /> Up next</h2></div>
            {upNext.length ? <ul className="tlist">{upNext.map((t) => <TaskLine key={t.id} t={t} people={people} />)}</ul> : <Empty>Nothing dated yet.</Empty>}
          </section>
          <Workload s={s} people={people} />
          <ProjectFiles p={p} resources={resources} />
        </div>
      </div>
    </>
  );
}

// Milestones on one line from today to the last one, so "how far is the finish" reads at a glance.
function MilestoneRoad({ s }: { s: Stats }) {
  const { open } = useTaskDrawer();
  const dated = s.milestones.filter((m) => m.end_date);
  if (!dated.length) return null;
  const t0 = today();
  const first = [t0, ...dated.map((m) => m.end_date!)].sort()[0];
  const last = [t0, ...dated.map((m) => m.end_date!)].sort().at(-1)!;
  const span = Math.max(1, daysUntil(last) - daysUntil(first));
  const at = (iso: string) => 7 + ((daysUntil(iso) - daysUntil(first)) / span) * 84;
  return (
    <section className="card road">
      <div className="card-h"><h2><Diamond size={14} aria-hidden /> Milestones</h2><span className="muted sm">{s.milestones.filter((m) => m.status === 'done').length} of {s.milestones.length} reached</span></div>
      <div className="road-track">
        <div className="road-line" /><div className="road-fill" style={{ width: `${at(t0)}%` }} />
        <div className="road-today" style={{ left: `${at(t0)}%` }}><span>Today</span></div>
        {dated.map((m, i) => (
          <button key={m.id} className={`road-ms ${m.status === 'done' ? 'done' : isOverdue(m) ? 'late' : ''} b-${m.date_basis} ${i % 2 ? 'low' : ''}`} style={{ left: `${at(m.end_date!)}%` }} onClick={() => open(m.id)}>
            <i /><span><b>{m.title}</b><small>{fmtDate(m.end_date)} · {basisLabel[m.date_basis]}</small></span>
          </button>
        ))}
      </div>
    </section>
  );
}

function StatusCard({ p, s, people, posting, setPosting }: { p: Project; s: Stats; people: Person[]; posting: boolean; setPosting: (b: boolean) => void }) {
  const save = useSave();
  const [health, setHealth] = useState<Health>(s.health ?? 'on_track');
  const [text, setText] = useState('');
  const draft = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('question', `Draft a 2-3 sentence status update for project ${p.key} (${p.title}): what moved recently, what's next, and any risk. First person plural, plain text, no preamble, no headings.`);
      return api<{ answer: string }>('/ask', { method: 'POST', body: fd });
    },
    onSuccess: (r) => setText(r.answer.trim()),
  });
  const log = [...(p.status_log ?? [])].sort((a, b) => b.at.localeCompare(a.at));
  return (
    <section className="card status-card">
      <div className="card-h"><h2><Megaphone size={14} aria-hidden /> Status</h2>{!posting && <button className="btn sm" onClick={() => setPosting(true)}>Post update</button>}</div>
      {posting && (
        <form className="status-form" onSubmit={async (e) => { e.preventDefault(); await save.mutateAsync({ path: `/projects/${p.id}/status`, body: { health, text } }); setText(''); setPosting(false); }}>
          <div className="seg health-seg">{(['on_track', 'at_risk', 'off_track'] as Health[]).map((h) => <button type="button" key={h} className={`${health === h ? 'on' : ''} h-${h}`} onClick={() => setHealth(h)}><i />{healthLabel[h]}</button>)}</div>
          <textarea rows={3} autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="How's it going? What moved, what's next, what's in the way." aria-label="Status update" />
          <div className="row">
            <button className="btn primary" disabled={!text.trim() || save.isPending}>Post to {people.length > 1 ? 'the team' : 'the log'}</button>
            <button type="button" className="btn" onClick={() => draft.mutate()} disabled={draft.isPending}><OpsMark size={14} aria-hidden /> {draft.isPending ? 'Ops is drafting…' : 'Draft with Ops'}</button>
            <span className="spacer" /><button type="button" className="btn ghost" onClick={() => setPosting(false)}>Cancel</button>
          </div>
          <Err e={save.error ?? draft.error} />
        </form>
      )}
      {log.length ? (
        <ol className="status-log">{log.slice(0, posting ? 2 : 4).map((u, i) => (
          <li key={u.at} className={i === 0 ? 'latest' : ''}>
            <span className={`hdot h-${u.health}`} title={healthLabel[u.health]} />
            <div><p>{u.text}</p><span className="muted sm"><Avatar email={u.by} people={people} size={16} /> {firstName(u.by, people)} · {ago(u.at)} · {healthLabel[u.health]}</span></div>
          </li>
        ))}</ol>
      ) : !posting && (
        <div className="status-empty">
          <p><b>No status update yet.</b> A two-line update tells your partner where {p.key} stands without a meeting, and shows up on their command center.</p>
          <button className="btn" onClick={() => setPosting(true)}><Megaphone size={14} aria-hidden /> Write the first one</button>
        </div>
      )}
      {log.length > 0 && !s.manual && !posting && <p className="notice sm">Last update was {ago(log[0].at)}. Health is being read from the plan until someone posts again.</p>}
    </section>
  );
}

// The fix-it list: each problem comes with the one-click action that clears it.
function Attention({ s, me, people }: { s: Stats; me: Me; people: Person[] }) {
  const save = useSave();
  const others = people.filter((x) => x.email !== me.email && x.active);
  const claim = (t: Task, owner: string) => save.mutate({ path: `/tasks/${t.id}`, method: 'PATCH', body: { owner, expected_version: t.version } });
  const items = [
    ...s.overdue.map((t) => ({ t, why: 'Overdue', tone: 'bad', icon: AlertTriangle })),
    ...s.blocked.filter((t) => !isOverdue(t)).map((t) => ({ t, why: t.blocker ? `Blocked: ${t.blocker}` : 'Blocked', tone: 'bad', icon: AlertTriangle })),
    ...s.unowned.filter((t) => !isOverdue(t) && t.status !== 'blocked').sort(byDate).map((t) => ({ t, why: 'No owner', tone: 'warn', icon: UserX })),
  ];
  const [all, setAll] = useState(false);
  const { open } = useTaskDrawer();
  return (
    <section className="card">
      <div className="card-h"><h2><AlertTriangle size={14} aria-hidden /> Needs attention</h2><span className="muted sm">{items.length || ''}</span></div>
      {items.length ? (
        <ul className="attn">
          {items.slice(0, all ? 99 : 6).map(({ t, why, tone, icon: Icon }) => (
            <li key={t.id + why}>
              <Icon size={14} className={tone} aria-hidden />
              <div><button className="link strong" onClick={() => open(t.id)}>{t.title}</button><span className={`sm ${tone}`}>{why}{t.end_date ? ` · ${fmtDate(t.end_date)}` : ''}</span></div>
              {!t.owner ? (
                <span className="row">
                  <button className="btn sm primary" onClick={() => claim(t, me.email)}><UserPlus size={13} aria-hidden /> Claim</button>
                  {others.map((o) => <button key={o.email} className="btn sm" onClick={() => claim(t, o.email)}>→ {firstName(o.email, people)}</button>)}
                </span>
              ) : <span className="row sm muted"><Avatar email={t.owner} people={people} size={18} /> {firstName(t.owner, people)}</span>}
            </li>
          ))}
        </ul>
      ) : <p className="all-clear"><Check size={16} aria-hidden /> Nothing overdue, blocked or unowned. Nice.</p>}
      {items.length > 6 && <button className="link sm" onClick={() => setAll(!all)}>{all ? 'Show fewer' : `Show all ${items.length}`}</button>}
    </section>
  );
}

function Workload({ s, people }: { s: Stats; people: Person[] }) {
  const owners = [...people.filter((x) => s.open.some((t) => t.owner === x.email)).map((x) => x.email), null];
  const max = Math.max(1, ...owners.map((o) => s.open.filter((t) => t.owner === o).length));
  return (
    <section className="card">
      <div className="card-h"><h2>Who's on it</h2></div>
      <ul className="workload">{owners.map((o) => {
        const n = s.open.filter((t) => t.owner === o).length;
        if (!n) return null;
        return <li key={o ?? 'none'}>{o ? <Avatar email={o} people={people} size={22} /> : <span className="avatar none" style={{ width: 22, height: 22 }}>?</span>}
          <span>{o ? firstName(o, people) : 'Unowned'}</span><div className={`wl-bar ${o ? '' : 'none'}`}><i style={{ width: `${(n / max) * 100}%` }} /></div><b>{n}</b></li>;
      })}</ul>
    </section>
  );
}

function ProjectFiles({ p, resources }: { p: Project; resources: Resource[] }) {
  const save = useSave();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState(''); const [url, setUrl] = useState('');
  return (
    <section className="card">
      <div className="card-h"><h2><Pin size={14} aria-hidden /> Files & links</h2>{!adding && <button className="btn sm" onClick={() => setAdding(true)}><Plus size={13} aria-hidden /> Pin</button>}</div>
      {resources.length ? <ul className="res">{resources.map((r) => (
        <li key={r.id}><ExternalLink size={14} aria-hidden /><a href={r.url} target="_blank" rel="noreferrer">{r.title}</a>
          <button className="icon" aria-label={`Unpin ${r.title}`} onClick={() => save.mutate({ path: `/resources/${r.id}`, method: 'DELETE' })}><Trash2 size={13} /></button></li>
      ))}</ul> : !adding && <Empty>Pin the spec, the folder, the quote: whatever you open every time you work on {p.key}.</Empty>}
      {adding && (
        <form className="row" onSubmit={async (e) => { e.preventDefault(); await save.mutateAsync({ path: '/resources', body: { title, url, project: p.id } }); setTitle(''); setUrl(''); setAdding(false); }}>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Name" aria-label="Name" required />
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://drive.google.com/…" aria-label="Link" required />
          <button className="btn primary sm">Pin</button><button type="button" className="btn ghost sm" onClick={() => setAdding(false)}>Cancel</button>
        </form>
      )}
      <Err e={save.error} />
    </section>
  );
}

function TaskLine({ t, people }: { t: Task; people: Person[] }) {
  const { open } = useTaskDrawer();
  return <li><span className="row" style={{ minWidth: 0, flexWrap: 'nowrap' }}><KindIcon t={t} /><button className="link" onClick={() => open(t.id)}>{t.title}</button></span>
    <span className="row" style={{ flexWrap: 'nowrap' }}><DateText t={t} /><Avatar email={t.owner} people={people} size={18} /></span></li>;
}

const KindIcon = ({ t }: { t: Task }) => t.kind === 'milestone' ? <Diamond size={13} className="kind-ic ms" aria-label="Milestone" />
  : t.kind === 'decision' ? <Scale size={13} className="kind-ic dec" aria-label="Decision" /> : null;

// ---------- List ----------

const GROUPS: Status[] = ['active', 'blocked', 'planned', 'done'];

function ListView({ p, s, me, people }: { p: Project; s: Stats; me: Me; people: Person[] }) {
  const [folded, setFolded] = useState<Record<string, boolean>>({ done: true });
  const [mineOnly, setMineOnly] = useState(false);
  const shown = mineOnly ? s.tasks.filter((t) => t.owner === me.email) : s.tasks;
  // Subtasks sit right under their parent when both are in the same group.
  const ordered = (list: Task[]) => {
    const ids = new Set(list.map((t) => t.id));
    const tops = list.filter((t) => !t.parent_id || !ids.has(t.parent_id)).sort(byDate);
    return tops.flatMap((t) => [t, ...list.filter((c) => c.parent_id === t.id).sort(byDate)]);
  };
  return (
    <section className="card flush lv">
      <div className="lv-bar row">
        <div className="seg"><button className={!mineOnly ? 'on' : ''} onClick={() => setMineOnly(false)}>Everyone</button><button className={mineOnly ? 'on' : ''} onClick={() => setMineOnly(true)}>Mine</button></div>
        <span className="muted sm">Press <kbd>N</kbd> to add a task</span>
      </div>
      <div className="lv-head"><span /><span>Task</span><span>Priority</span><span>Owner</span><span>Date</span><span>Type</span></div>
      {GROUPS.map((g, gi) => {
        const list = ordered(shown.filter((t) => t.status === g));
        if (!list.length && (g === 'blocked' || g === 'done')) return null;
        return (
          <div key={g} className={`lv-group g-${g}`}>
            <button className="lv-gh" onClick={() => setFolded({ ...folded, [g]: !folded[g] })}>
              {folded[g] ? <ChevronRight size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}<span className={`pill s-${g}`}>{statusLabel[g]}</span><span className="count">{list.length}</span>
            </button>
            {!folded[g] && <>
              {list.map((t) => <ListRow key={t.id} t={t} people={people} sub={!!t.parent_id && list.some((x) => x.id === t.parent_id)} />)}
              {g !== 'done' && <QuickAdd project={p.id} status={g} me={me} hotkey={gi === 0} />}
            </>}
          </div>
        );
      })}
    </section>
  );
}

function ListRow({ t, people, sub }: { t: Task; people: Person[]; sub: boolean }) {
  const save = useSave();
  const { open } = useTaskDrawer();
  const [pop, setPop] = useState(false);
  const patch = (body: Record<string, unknown>) => save.mutate({ path: `/tasks/${t.id}`, method: 'PATCH', body: { ...body, expected_version: t.version } });
  const done = t.status === 'done';
  return (
    <div className={`lv-row ${done ? 'is-done' : ''} ${sub ? 'sub' : ''}`}>
      <button className={`check ${done ? 'on' : ''} ${pop ? 'pop' : ''}`} aria-label={done ? 'Mark not done' : 'Mark done'}
        onClick={() => { if (!done) { setPop(true); setTimeout(() => setPop(false), 500); } patch({ status: done ? 'active' : 'done' }); }}><Check size={12} aria-hidden /></button>
      <button className="lv-title" onClick={() => open(t.id)}>{t.title}{recentlyByAgent(t) && <OpsMark size={12} className="agent-ic" aria-label="Changed by Ops" />}<CommentCount n={t.comment_count} /></button>
      <PriorityFlag p={t.priority} onChange={(priority) => patch({ priority })} />
      <label className="owner-pick" title={t.owner ? firstName(t.owner, people) : 'Assign'}>
        {t.owner ? <Avatar email={t.owner} people={people} size={22} /> : <span className="avatar none" style={{ width: 22, height: 22 }}><UserPlus size={12} /></span>}
        <select value={t.owner ?? ''} onChange={(e) => patch({ owner: e.target.value || null })} aria-label="Owner">
          <option value="">Unassigned</option>{people.map((x) => <option key={x.email} value={x.email}>{x.name || x.email}</option>)}</select>
      </label>
      <span className="lv-date">{t.date_basis === 'unknown' && !done
        ? <input type="date" className="date-in" aria-label={`Target date for ${t.title}`} onChange={(e) => e.target.value && patch({ date_basis: 'target', end_date: e.target.value })} />
        : <DateText t={t} />}</span>
      <span className="lv-kind"><KindIcon t={t} /> {t.kind === 'task' ? '' : t.kind === 'milestone' ? 'Milestone' : 'Decision'}</span>
    </div>
  );
}

function QuickAdd({ project, status, me, hotkey }: { project: string; status: Status; me: Me; hotkey: boolean }) {
  const save = useSave();
  const [title, setTitle] = useState('');
  const [on, setOn] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!hotkey) return;
    const k = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.key.toLowerCase() === 'n' && !e.metaKey && !e.ctrlKey && !/INPUT|TEXTAREA|SELECT/.test(el.tagName) && !el.isContentEditable) { e.preventDefault(); setOn(true); setTimeout(() => ref.current?.focus()); }
    };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [hotkey]);
  if (!on) return <button className="lv-add" onClick={() => { setOn(true); setTimeout(() => ref.current?.focus()); }}><Plus size={13} aria-hidden /> Add task</button>;
  return (
    <form className="lv-add-form" onSubmit={async (e) => { e.preventDefault(); if (!title.trim()) return; await save.mutateAsync({ path: '/tasks', body: { title: title.trim(), project, status, owner: me.email } }); setTitle(''); }}>
      <Plus size={13} aria-hidden />
      <input ref={ref} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task name, then Enter. Esc to stop." aria-label="New task"
        onKeyDown={(e) => e.key === 'Escape' && (setOn(false), setTitle(''))} onBlur={() => !title && setOn(false)} />
      <span className="muted sm">assigned to you</span>
      <Err e={save.error} />
    </form>
  );
}

// ---------- Board ----------

function BoardView({ s, people }: { s: Stats; people: Person[] }) {
  const save = useSave();
  const { open } = useTaskDrawer();
  const [over, setOver] = useState<Status | null>(null);
  const recentDone = (t: Task) => (t.done_on ?? '') > new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
  return (
    <div className="board">
      {GROUPS.map((g) => {
        const list = s.tasks.filter((t) => t.status === g && (g !== 'done' || recentDone(t))).sort(byDate);
        return (
          <section key={g} className={`bcol g-${g} ${over === g ? 'over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOver(g); }} onDragLeave={() => setOver(null)}
            onDrop={(e) => { e.preventDefault(); setOver(null); const id = e.dataTransfer.getData('text/task'); const t = s.tasks.find((x) => x.id === id);
              if (t && t.status !== g) save.mutate({ path: `/tasks/${t.id}`, method: 'PATCH', body: { status: g, expected_version: t.version } }); }}>
            <h2><span className={`pill s-${g}`}>{statusLabel[g]}</span><span className="count">{list.length}</span></h2>
            {list.map((t) => (
              <article key={t.id} className={`bcard ${isOverdue(t) ? 'late' : ''}`} draggable onDragStart={(e) => e.dataTransfer.setData('text/task', t.id)} onClick={() => open(t.id)}>
                <span className="bcard-t"><KindIcon t={t} />{t.title}</span>
                {t.status === 'blocked' && t.blocker && <span className="sm bad clamp">{t.blocker}</span>}
                <span className="bcard-f sm"><DateText t={t} /><span className="spacer" />{recentlyByAgent(t) && <OpsMark size={12} className="agent-ic" aria-label="Changed by Ops" />}
                  {t.owner ? <Avatar email={t.owner} people={people} size={20} /> : <span className="avatar none" style={{ width: 20, height: 20 }} title="No owner">?</span>}</span>
              </article>
            ))}
            {!list.length && <p className="bempty">Drop tasks here</p>}
          </section>
        );
      })}
      <Err e={save.error} />
    </div>
  );
}

// ---------- Activity ----------

const fieldLabel: Record<string, string> = { status: 'status', owner: 'owner', end_date: 'due date', start_date: 'start date', date_basis: 'date basis', title: 'title',
  details: 'details', dependencies: 'dependencies', blocker: 'blocker', parent_id: 'parent', kind: 'type', project: 'project', linked_drive_url: 'Drive link', sort: 'order' };

function ActivityView({ p, people }: { p: Project; people: Person[] }) {
  const q = useQuery({ queryKey: ['project-activity', p.id], queryFn: () => api<{ activity: Activity[] }>(`/projects/${p.id}/activity`) });
  const { open } = useTaskDrawer();
  // Runs of the same person doing the same thing for the same reason (a drafted baseline, an import) read as one entry.
  const feed = useMemo(() => {
    const updates = (p.status_log ?? []).map((u) => ({ kind: 'status' as const, at: u.at, u }));
    const acts = (q.data?.activity ?? []).map((a) => ({ kind: 'task' as const, at: a.at, a, more: [] as Activity[] }));
    const sorted = [...updates, ...acts].sort((x, y) => y.at.localeCompare(x.at));
    const out: typeof sorted = [];
    for (const x of sorted) {
      const prev = out.at(-1);
      if (x.kind === 'task' && prev?.kind === 'task' && x.a.reason && prev.a.reason === x.a.reason && prev.a.actor === x.a.actor && prev.a.action === x.a.action) prev.more.push(x.a);
      else out.push(x);
    }
    return out;
  }, [q.data, p.status_log]);
  const describe = (a: Activity) => {
    if (a.action === 'create') return 'created';
    if (a.action === 'archive') return 'archived';
    const keys = Object.keys(a.after ?? {}).filter((k) => k !== 'done_on' && k !== 'sort');
    if (keys.length === 1 && keys[0] === 'status') return <>moved to <span className={`pill s-${a.after.status}`}>{statusLabel[a.after.status as Status]}</span></>;
    if (keys.length === 1 && keys[0] === 'owner') return a.after.owner ? <>assigned {firstName(a.after.owner, people)} to</> : 'unassigned';
    if (keys.includes('end_date') && a.after.end_date) return <>moved the date to <b>{fmtDate(a.after.end_date)}</b> on</>;
    return `changed ${keys.map((k) => fieldLabel[k] ?? k).join(', ')} on`;
  };
  return (
    <section className="card">
      <Err e={q.error} />
      {q.isLoading && <p className="muted">Loading…</p>}
      <ol className="feed">
        {feed.map((x, i) => x.kind === 'status' ? (
          <li key={`s${i}`} className="feed-status"><span className={`hdot h-${x.u.health}`} />
            <div><b>{firstName(x.u.by, people)}</b> posted an update · <span className="muted sm">{ago(x.at)}</span><p>{x.u.text}</p></div></li>
        ) : (
          <li key={`a${i}`}><Avatar email={x.a.actor} people={people} size={22} />
            <div><b>{firstName(x.a.actor, people)}</b>{x.a.on_behalf_of && <span className="muted"> for {firstName(x.a.on_behalf_of, people)}</span>} {describe(x.a)}{' '}
              <button className="link" onClick={() => open(x.a.task_id)}>{x.a.task_title}</button>
              {x.more.length > 0 && <span> and {x.more.length} more</span>} <span className="muted sm">· {ago(x.at)}</span>
              {x.a.reason && <p className="muted sm feed-why">{x.a.actor === 'operator@agents.gbsoft.dev' && <OpsMark size={11} aria-hidden />} {x.a.reason}</p>}
              {x.more.length > 0 && <details className="feed-more sm"><summary className="muted">Show all {x.more.length + 1}</summary>
                {[x.a, ...x.more].map((m, j) => <button key={j} className="link" onClick={() => open(m.task_id)}>{m.task_title}</button>)}</details>}</div></li>
        ))}
      </ol>
      {q.data && !feed.length && <Empty>Nothing has happened here yet.</Empty>}
    </section>
  );
}
